import React, { createElement, FC, Fragment, useEffect, useRef } from 'react';
import { render } from 'react-dom';

import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import { css, ThemeProvider, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { SearchClient } from 'algoliasearch';

import { IdentifierDTO, Magnifier } from '@zazume/zzm-base';

import { useI18n } from '#/hooks/useI18n';

import { useRouter } from '../../../hooks';
import { fromIndexNameToAlgolia, IndexName } from '../../../utils/algolia/algoliaHelper';
import { ResultGoToItem, ResultItemBase } from './ResultItem';

const Container = styled.div`
  display: flex;
  flex: 1;
`;

const DetachedContainer = styled.div`
  display: none;
`;

const MagnifierButton = styled.span(
  ({ theme }) => css`
    width: ${theme.spacing.s12};
    height: ${theme.spacing.s12};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
      svg > path {
        stroke: ${theme.colors.Primary};
      }
    }
  `
);

interface AutocompleteProps {
  openOnFocus?: boolean;
  searchClient: SearchClient;
  organizationId: IdentifierDTO;
  indexPrefix: string;
  indexes: IndexName[];
  renderItemsAs: ResultItemBase<{}>;
  placeholder?: string;
  detached?: boolean;
}

export const Autocomplete: FC<AutocompleteProps> = props => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useI18n();
  const { navigate } = useRouter();

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const Component: any = props.renderItemsAs;
    const toQueries = (query: string) =>
      props.indexes.map(index => ({
        indexName: fromIndexNameToAlgolia(props.indexPrefix, props.organizationId)(index),
        query,
        params: {
          hitsPerPage: 10
        }
      }));

    const search = autocomplete({
      container: containerRef.current,
      defaultActiveItemId: 0,
      renderer: { createElement: createElement as any, Fragment },
      placeholder: props.placeholder ?? t('globalSearch.placeholder'),
      detachedMediaQuery: props.detached ? '' : 'none',
      translations: { detachedCancelButtonText: t('buttons.cancel') },
      render({ children }, root) {
        render(children as any, root);
      },
      getSources: ({ query }) => [
        {
          sourceId: props.indexes.join('_'),
          getItems: () => {
            return getAlgoliaResults({
              searchClient: props.searchClient,
              queries: toQueries(query)
            });
          },
          templates: {
            item({ item, components }) {
              return (
                <ThemeProvider theme={theme}>
                  <Component hit={item} components={components} t={t} />
                </ThemeProvider>
              );
            }
          },
          onSelect(params) {
            navigate(ResultGoToItem(params.item as any));
          }
        }
      ]
    });

    return () => {
      search.destroy();
    };
  }, [props, t, theme, navigate]);

  // setIsOpen(true) cannot be used here: the focus dispatch resets isOpen and instantly
  // closes the detached modal, so we trigger the library's own (hidden) detached button
  const handleOpenDetached = () => {
    containerRef.current?.querySelector<HTMLButtonElement>('.aa-DetachedSearchButton')?.click();
  };

  if (props.detached) {
    return (
      <>
        <DetachedContainer ref={containerRef} />
        <MagnifierButton onClick={handleOpenDetached}>
          <Magnifier scale="lg" color="Gray500" />
        </MagnifierButton>
      </>
    );
  }

  return <Container ref={containerRef} />;
};
